import { useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ApiError, get, type Destination as ApiDestination, type DestinationInput, type DestProtocol, type FillMode, type FillQuality, type Ingest, type InputTrack, type Profile, type StreamKey } from '../api';
import type { Destination, Processing } from './types';
import { ORIGINAL, PLATFORM_LABEL, platformOf } from './adapter';
import { Banner, DangerLink, Field, InlineConfirm, LogoSlot, PrimaryButton, SecondaryButton, Switch } from './ui';
import { LanguageSettings, languageErrors } from './LanguageSettings';
import { FillSettings } from './FillSettings';
import { GraceSettings } from './GraceSettings';
import { ProtocolSettings, choiceOf, protocolName, urlErrorFor } from './ProtocolSettings';
import { t } from '../i18n';
import { langName } from './captions';
import { OPTIMIZABLE } from './transcode';
import { TrackChoice, showTracks } from './TrackChoice';
import { extraCodecs, sendCodecs } from './tracks';
import { VerticalSource } from './VerticalSource';
import { portrait as portraitOf } from './vertical';
import type { DestFallback, DestInput } from '../api';

/** Language and captions are designed but not wired to the backend yet. */
const LANGUAGES = import.meta.env.VITE_FEATURE_LANGUAGES === '1';

const profileLabel = (p: Profile) => (p.id === 'custom' ? 'RTMP' : p.name);

/**
 * Inline editor of a destination (or a new one) inside its card. Saving or
 * removing goes to the API; errors stay in the card and the old settings keep
 * working. Confirmations are inline too: no modal on top of a live stream.
 */
export function DestinationEditor({ dest, saved, isNew, profiles, streamLive, fill, captionsAllowed, translationAllowed, tracks, onCancel, onSubmit, onRemove }: {
  dest?: Destination; saved?: ApiDestination; isNew?: boolean; profiles: Profile[]; streamLive: boolean;
  /** E1: the Enhanced RTMP tracks of the input now (the track choice) */
  tracks?: InputTrack[];
  /** features.fill: the advanced settings offer the bitrate for the platform (existing destinations) */
  fill?: boolean;
  /** the account has captions (an admin turned them on) */
  captionsAllowed?: boolean;
  /** the account may translate captions (CAP-8): the Captions language of a YouTube output */
  translationAllowed?: boolean;
  onCancel: () => void; onSubmit: (body: DestinationInput) => Promise<void>; onRemove?: () => Promise<void>;
}) {
  const initial = { name: dest?.name ?? '', url: saved?.url ?? '', key: '' };
  const [form, setForm] = useState(initial);
  const [profile, setProfile] = useState<string | null>(saved?.profile ?? null);
  const [lang, setLang] = useState<Processing>(dest?.processing ?? ORIGINAL);
  const [keyShown, setKeyShown] = useState(false);
  // Captions over HTTP (YouTube, docs/captions-overlay.md §10.6): the switch and the optional Studio address.
  const capInitial = !!saved?.captions_http;
  const [capOn, setCapOn] = useState(capInitial);
  const [capURL, setCapURL] = useState('');
  const [capURLShown, setCapURLShown] = useState(false);
  const [capRemoveURL, setCapRemoveURL] = useState(false);
  // Translated captions (CAP-8, docs/captions-translation.md §5, §9): '' the original, else a translation.
  const capLang0 = saved?.captions_lang ?? '';
  const [capLang, setCapLang] = useState(capLang0);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [confirm, setConfirm] = useState<'remove' | 'discard' | 'protocol' | 'captions-lang' | undefined>();
  // Bitrate for the platform: the destination's own choice, null = the default (fill_effective says which).
  const fillOn = !!fill && !isNew && !!saved?.fill_effective;
  const fill0 = { mode: saved?.fill_mode ?? null, quality: saved?.fill_quality ?? null };
  const [fillChoice, setFillChoice] = useState<{ mode: FillMode | null; quality: FillQuality | null }>(fill0);
  const [advanced, setAdvanced] = useState(false);
  // TRANS-1: optimize for the platform (off by default, owner 22.10); a preset id set by an admin counts as on.
  const tc0 = saved?.transcode ?? 'off';
  const [tcOn, setTcOn] = useState(tc0 !== 'off');
  // CAPTIONS-DELIVERY-1: captions on a platform other than YouTube (608 or burned in, the center's choice); off per destination.
  const cd0 = saved?.captions_delivery !== 'off';
  const [cdOn, setCdOn] = useState(cd0);
  // Hold when OBS drops (source grace, D21): the destination's own, null = as its stream key's.
  const grace0 = saved?.source_grace_ms ?? null;
  const [grace, setGrace] = useState<number | null>(grace0);
  // E1: the tracks this output gets (null = 0); a change switches at the next keyframe, no reconnect.
  const vt0 = saved?.video_track ?? 0;
  const at0 = saved?.audio_track ?? 0;
  const [vt, setVt] = useState(vt0);
  const [at, setAt] = useState(at0);
  const trackDirty = !isNew && (vt !== vt0 || at !== at0);
  // E1h: HEVC/AV1 as they are (profiles with more than H.264, by RTMPS); a change reconnects the output.
  const enh0 = !!saved?.enhanced_codecs;
  const [enh, setEnh] = useState(enh0);
  const [graceBad, setGraceBad] = useState(false);
  // VERT-1 (mockup C / C2): what it sends of the pair; absent from a center before VERT-1 = not shown
  const in0: DestInput = saved?.input ?? 'main';
  const fb0: DestFallback = saved?.fallback ?? 'main';
  const [inp, setInp] = useState<DestInput>(in0);
  const [fb, setFb] = useState<DestFallback>(fb0);
  const inDirty = inp !== in0;
  const fbDirty = fb !== fb0;
  const keys = useQuery({ queryKey: ['stream-keys'], queryFn: () => get<StreamKey[]>('/api/stream-keys'), enabled: !isNew || !!translationAllowed });
  const ingest = useQuery({ queryKey: ['ingest'], queryFn: () => get<Ingest>('/api/ingest'), enabled: !isNew });
  const streamKey = keys.data?.find((k) => k.id === saved?.stream_key_id);
  // the vertical input was turned on once for this stream (VERT-1): the source choice is shown
  const verticalKnown = !!streamKey?.vertical;
  const keyGrace = streamKey?.source_grace_ms ?? null;
  const inherited = keyGrace ?? ingest.data?.default_source_grace_ms;

  const p = profiles.find((x) => x.id === profile);
  const platform = profile ? platformOf(profile) : null;
  // Protocol to the platform (YouTube: RTMPS / HLS, docs/youtube-hls.md §4): a switch is a new URL, the output reconnects.
  const protocols = isNew ? [] : choiceOf(p);
  const proto0: DestProtocol = saved?.protocol ?? 'rtmps';
  const [protocol, setProtocol] = useState<DestProtocol>(proto0);
  const protoDirty = protocols.length > 0 && protocol !== proto0;
  const hasExtra = extraCodecs(p).length > 0;
  const enhNow = enh && protocol !== 'hls';
  const enhDirty = !isNew && hasExtra && enhNow !== enh0;
  const fillDirty = fillOn && (fillChoice.mode !== fill0.mode || fillChoice.quality !== fill0.quality);
  const capLangDirty = capLang !== capLang0;
  // Between the original and a translation the output's video delay changes: that reconnects it (§6).
  const capLangReconnects = capLangDirty && (capLang === '') !== (capLang0 === '');
  const tcDirty = !isNew && tcOn !== (tc0 !== 'off');
  const cdDirty = !isNew && cdOn !== cd0;
  const capDirty = capOn !== capInitial || capURL !== '' || capRemoveURL || capLangDirty || cdDirty;
  const fieldsDirty = form.name !== initial.name || form.url !== initial.url || form.key !== '' || (isNew && !!profile) || protoDirty || inDirty || fbDirty;
  const graceDirty = !isNew && grace !== grace0;
  const dirty = fieldsDirty || fillDirty || graceDirty || capDirty || tcDirty || trackDirty || enhDirty;
  /** only the bitrate, the hold and / or the captions changed: the request carries just those, and the output keeps its connection */
  const settingsOnly = (fillDirty || graceDirty || capDirty || tcDirty || trackDirty) && !fieldsDirty && !enhDirty;
  const tracksOnly = trackDirty && !fieldsDirty && !fillDirty && !graceDirty && !capDirty && !tcDirty;
  const captionsOnly = capDirty && !fieldsDirty && !fillDirty && !graceDirty && !tcDirty && !capLangReconnects;
  const showTc = !isNew && !!saved && (OPTIMIZABLE.has(saved.profile) || tc0 !== 'off');
  const showCaptions = profile === 'youtube' && (!!captionsAllowed || capInitial);
  const showCd = !isNew && !!saved && saved.profile !== 'youtube' && (!!captionsAllowed || !cd0);
  const cdMethod = saved?.captions_delivery_effective?.method;
  const keyForCaptions = streamKey ?? keys.data?.find((k) => !k.revoked_at);
  const streamLang = keyForCaptions?.captions_language;
  const capLangs = [...new Set([...(keyForCaptions?.captions_translate ?? []), ...(capLang ? [capLang] : [])])];
  const showCapLang = (showCaptions ? capOn : showCd && cdOn) && (!!translationAllowed || capLang0 !== '') && (capLangs.length > 0 || capLang0 !== '');
  const capURLError = capURL && !/^https?:\/\/([^/]+\.)?youtube\.com(\/|\?|$)/i.test(capURL.trim()) ? 'Paste the http(s)://…youtube.com address YouTube Studio shows' : undefined;
  const urlError = urlErrorFor(form.url, protocol);
  const missing = isNew && (!profile || !form.url.trim() || !form.key);
  const langErr = LANGUAGES ? languageErrors(lang) : { voiceUnavailable: false, sameLanguage: false };
  const invalid = !!urlError || !!missing || langErr.voiceUnavailable || langErr.sameLanguage || (graceDirty && graceBad) || !!capURLError;
  const live = streamLive && !!dest?.enabled;
  const name = form.name || (p ? profileLabel(p) : 'this destination');

  const set = (k: keyof typeof form) => (v: string) => { setForm((f) => ({ ...f, [k]: v })); setSaveError(''); };
  const cancel = () => (dirty ? setConfirm('discard') : onCancel());

  const pickProtocol = (v: DestProtocol) => {
    // the server address follows the protocol unless the user typed their own; back to the saved one restores it
    const was = protocols.find((x) => x.id === protocol)?.url ?? '';
    const next = v === proto0 ? initial.url : protocols.find((x) => x.id === v)?.url;
    if (next !== undefined && (!form.url.trim() || form.url.trim() === was || form.url === initial.url)) setForm((f) => ({ ...f, url: next }));
    setProtocol(v);
    // with HLS the platform takes H.264 only: the center refuses enhanced_codecs there (400), so it goes off with it
    if (v === 'hls') setEnh(false);
    setSaveError('');
  };

  const submit = async (e?: FormEvent, confirmed = false) => {
    e?.preventDefault();
    if (!dirty || invalid || saving) return;
    // On air, a protocol switch reconnects the platform: ask first, inline like the other confirmations.
    if (protoDirty && live && !confirmed) {
      setConfirm('protocol');
      return;
    }
    if (capLangReconnects && live && !confirmed) {
      setConfirm('captions-lang');
      return;
    }
    setConfirm(undefined);
    setSaving(true);
    setSaveError('');
    const body: DestinationInput = settingsOnly ? {} : { name: form.name.trim() || (p ? profileLabel(p) : 'Destination'), url: form.url.trim() };
    if (form.key) body.key = form.key;
    if (isNew) Object.assign(body, { profile: profile!, enabled: true });
    if (protoDirty) body.protocol = protocol;
    if (fillDirty) {
      if (fillChoice.mode !== fill0.mode) body.fill_mode = fillChoice.mode;
      if (fillChoice.quality !== fill0.quality) body.fill_quality = fillChoice.quality;
    }
    if (graceDirty) body.source_grace_ms = grace;
    if (tcDirty) body.transcode = tcOn ? 'auto' : 'off';
    if (enhDirty) body.enhanced_codecs = enhNow;
    if (vt !== vt0) body.video_track = vt;
    if (inDirty) body.input = inp;
    if (fbDirty) body.fallback = fb;
    if (at !== at0) body.audio_track = at;
    if (showCaptions) {
      if (capOn !== capInitial || (isNew && capOn)) body.captions_http = capOn;
      if (capURL.trim()) body.captions_url = capURL.trim();
      else if (capRemoveURL) body.captions_url = '';
      if (capLangDirty) body.captions_lang = capLang;
    }
    if (showCd) {
      if (cdDirty) body.captions_delivery = cdOn ? 'auto' : 'off';
      if (capLangDirty) body.captions_lang = capLang;
    }
    try {
      await onSubmit(body);
    } catch (err) {
      // 409: the destination changed meanwhile (another tab or device): reload, then retry
      setSaveError(err instanceof ApiError && err.status === 409 ? t('proto.conflict')
        // E1h: the center's 400 for enhanced_codecs where the platform takes H.264 only, in words
        : err instanceof ApiError && err.status === 400 && /^enhanced_codecs/.test(err.message) ? t('dest.hevcRefused', { platform: dest ? PLATFORM_LABEL[dest.platform] : name, proto: protocolName(protocols, protocol) || protocol.toUpperCase() })
        // VERT-1: a destination that sends the vertical input is not transcoded yet (422 vertical_no_transcode), in words
        : err instanceof ApiError && err.code === 'vertical_no_transcode' ? t('vert.err.noTranscode', { platform: dest ? PLATFORM_LABEL[dest.platform] : name })
        : (err as Error).message);
      setSaving(false);
    }
  };

  const remove = async () => {
    setSaving(true);
    try {
      await onRemove?.();
    } catch (err) {
      setSaveError((err as Error).message);
      setSaving(false);
      setConfirm(undefined);
    }
  };

  return (
    <form onSubmit={submit} aria-label={isNew ? 'Add a destination' : `Edit ${dest?.name ?? ''}`} className="flex animate-condense flex-col gap-3 border-t border-line pt-3">
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-[11px] font-medium tracking-[.12em] text-accent">{isNew ? 'NEW DESTINATION' : 'DESTINATION SETTINGS'}</span>
        {dirty && !confirm && <span className="font-mono text-[11px] text-warn">● Unsaved changes</span>}
      </div>

      <div className={`flex flex-col gap-3 ${confirm ? 'opacity-50' : ''}`}>
        {isNew && (
          <div className="flex flex-col gap-1.5">
            <span className="text-xs text-muted">Platform</span>
            <div className="grid gap-1.5" style={{ gridTemplateColumns: `repeat(${Math.max(profiles.length, 1)}, minmax(0,1fr))` }}>
              {profiles.map((x) => (
                <button key={x.id} type="button" aria-pressed={profile === x.id} aria-label={profileLabel(x)} title={profileLabel(x)}
                  onClick={() => {
                    setProfile(x.id);
                    setForm((f) => ({ ...f, url: x.url ?? (f.url && profile && profiles.find((q) => q.id === profile)?.url === f.url ? '' : f.url), name: f.name || profileLabel(x) }));
                    setSaveError('');
                  }}
                  className={`flex min-h-11 flex-col items-center justify-center gap-0.5 rounded-md border py-1 ${profile === x.id ? 'border-live bg-live/[.12]' : 'border-line-2 bg-bg'}`}>
                  <LogoSlot platform={platformOf(x.id)} />
                </button>
              ))}
            </div>
          </div>
        )}

        <Field label="Name" name="name" value={form.name} placeholder="e.g. Main channel" disabled={saving} onChange={set('name')} />
        {!isNew && dest && (
          <div className="flex items-center justify-between gap-2 text-xs text-muted">
            <span>Platform</span>
            <span className="rounded-md border border-line px-2 py-[3px] font-mono text-xs text-text-soft">{PLATFORM_LABEL[dest.platform]}</span>
          </div>
        )}
        <Field label="Server URL" name="url" value={form.url} placeholder={protocol === 'hls' ? 'https://' : 'rtmp:// or rtmps://'} mono disabled={saving || (isNew && !profile)} error={urlError}
          hint={protocol === 'hls' ? t('proto.serverHls') : p?.url ? 'Filled in for this platform. Change it only if the platform shows a different one.' : platform === 'rtmp' ? 'Any RTMP or RTMPS server' : undefined}
          onChange={set('url')} />
        <Field label="Stream key" name="key" value={form.key} type={keyShown ? 'text' : 'password'} mono autoComplete="off" disabled={saving || (isNew && !profile)}
          placeholder={isNew ? 'Paste your stream key' : dest?.needsKeyFix ? 'Paste a new key' : 'Paste a new key to replace it'}
          warn={dest?.needsKeyFix && !form.key ? 'The platform did not accept the saved settings. Check the key and the server.' : undefined}
          hint={keyShown ? 'We hide it again after saving.' : protoDirty && protocol === 'hls' && !form.key ? t('proto.keyNote') : !isNew && saved?.has_key ? 'A key is saved. Leave empty to keep it.' : p?.key_help}
          action={form.key ? { label: keyShown ? 'Hide' : 'Show', onClick: () => setKeyShown((v) => !v) } : undefined}
          onChange={set('key')} />

        {!isNew && dest && saved && saved.input !== undefined && (verticalKnown || in0 !== 'main' || portraitOf(saved.profile, profiles)) && (
          <VerticalSource platformLabel={PLATFORM_LABEL[dest.platform]} portrait={portraitOf(saved.profile, profiles)} input={inp} fallback={fb}
            dual={saved.dual_of != null} tracks={tracks} disabled={saving}
            onInput={(v) => { setInp(v); setSaveError(''); }} onFallback={(v) => { setFb(v); setSaveError(''); }} />
        )}

        {!isNew && dest && saved && (showTracks(tracks, vt0, at0) || enh0) && (
          <TrackChoice tracks={tracks} platformLabel={PLATFORM_LABEL[dest.platform]} video={vt} audio={at} disabled={saving}
            codecs={sendCodecs(p, protocol, enhNow)} extra={protocol === 'hls' ? [] : extraCodecs(p)}
            enhanced={hasExtra ? { value: enhNow, saved: enh0, hls: protocol === 'hls', beta: !!p?.enhanced_codecs_beta, onChange: (v) => { setEnh(v); setSaveError(''); } } : undefined}
            onVideo={(v) => { setVt(v); setSaveError(''); }} onAudio={(v) => { setAt(v); setSaveError(''); }} />
        )}

        {!isNew && dest && saved && (
          <div className="flex flex-col gap-2">
            <button type="button" data-testid="dest-advanced" aria-expanded={advanced} onClick={() => setAdvanced((v) => !v)}
              className="flex min-h-[44px] md:min-h-9 items-center justify-between gap-2 rounded-md border border-line-2 px-2.5 text-left text-[13px] text-text-soft hover:text-text">
              <span>Advanced</span>
              <span aria-hidden className="font-mono text-xs text-muted">{advanced ? '▴' : '▾'}{(fillDirty || graceDirty || protoDirty) && !advanced ? ' ●' : ''}</span>
            </button>
            {advanced && (
              <>
                {protocols.length > 0 && (
                  <ProtocolSettings testId="dest-protocol" protocols={protocols} value={protocol} saved={proto0} platformLabel={PLATFORM_LABEL[dest.platform]}
                    name={name} live={live} keySteps disabled={saving} onChange={pickProtocol} />
                )}
                <GraceSettings testId="dest-grace" value={grace} saved={grace0} inherited={inherited} inheritLabel="As the stream key"
                  inheritFrom={keyGrace !== null ? 'the stream key’s' : 'the stream key’s (the default)'} subject={PLATFORM_LABEL[dest.platform]} live={live} disabled={saving}
                  onChange={(v, bad) => { setGrace(v); setGraceBad(bad); setSaveError(''); }} />
                {fillOn && saved.fill_effective && (
                  <FillSettings platformLabel={PLATFORM_LABEL[dest.platform]} effective={saved.fill_effective} value={fillChoice} live={live} disabled={saving}
                    onChange={(v) => { setFillChoice(v); setSaveError(''); }} />
                )}
              </>
            )}
          </div>
        )}

        {showCaptions && (
          <div data-testid="captions-settings" className="flex flex-col gap-3 rounded-md border border-line-2 p-3">
            <Switch label="Send captions" on={capOn} onChange={(v) => { setCapOn(v); setSaveError(''); }}
              sub="Captions in your stream's language go to YouTube over HTTP next to the video. The video never waits for them. Choose the language in the Captions panel under the stage." />
            {capOn && (
              <Field label="Captions URL from YouTube Studio (optional)" name="captions_url" value={capURL} type={capURLShown ? 'text' : 'password'} mono autoComplete="off"
                disabled={saving} error={capURLError}
                placeholder={saved?.has_captions_url && !capRemoveURL ? 'A captions URL is saved' : 'Only if Studio shows a different address'}
                hint={saved?.has_captions_url && !capRemoveURL ? 'A captions URL is saved. Leave empty to keep it.' : 'Without it we build the address from your stream key. It contains your key: keep it private.'}
                action={capURL ? { label: capURLShown ? 'Hide' : 'Show', onClick: () => setCapURLShown((v) => !v) }
                  : saved?.has_captions_url && !capRemoveURL ? { label: 'Remove', onClick: () => setCapRemoveURL(true) } : undefined}
                onChange={(v) => { setCapURL(v); setSaveError(''); }} />
            )}
            {showCapLang && (
              <label className="flex flex-col gap-1">
                <span className="text-[13px] text-text-soft">{t('dest.capLang')}</span>
                <select aria-label={t('dest.capLang')} name="captions_lang" value={capLang} disabled={saving}
                  onChange={(e) => { setCapLang(e.target.value); setSaveError(''); }}
                  className="min-h-[34px] rounded-md border border-line-2 bg-bg px-2 font-mono text-xs text-text disabled:opacity-50">
                  <option value="">{t('dest.capLangOriginal')}</option>
                  {capLangs.map((l) => <option key={l} value={l} disabled={l === streamLang}>{langName(l)}</option>)}
                </select>
                <span data-testid="caplang-hint" className="text-xs text-muted">
                  {capLang === '' ? t('dest.capLangOrigHint') : capLang === streamLang ? t('translate.same') : t('dest.capLangDelayHint')}
                </span>
              </label>
            )}
          </div>
        )}

        {showCd && dest && (
          <div data-testid="captions-delivery-settings" className="flex flex-col gap-3 rounded-md border border-line-2 p-3">
            <Switch label={t('cd.setting')} on={cdOn} onChange={(v) => { setCdOn(v); setSaveError(''); }}
              sub={t(cdMethod === 'burn' ? 'cd.settingHint.burn' : cdMethod === 'http' ? 'cd.settingHint.http' : 'cd.settingHint.cea608', { platform: PLATFORM_LABEL[dest.platform] })} />
            {showCapLang && (
              <label className="flex flex-col gap-1">
                <span className="text-[13px] text-text-soft">{t('dest.capLang')}</span>
                <select aria-label={t('dest.capLang')} name="captions_lang" value={capLang} disabled={saving}
                  onChange={(e) => { setCapLang(e.target.value); setSaveError(''); }}
                  className="min-h-[34px] rounded-md border border-line-2 bg-bg px-2 font-mono text-xs text-text disabled:opacity-50">
                  <option value="">{t('dest.capLangOriginal')}</option>
                  {capLangs.map((l) => <option key={l} value={l} disabled={l === streamLang}>{langName(l)}</option>)}
                </select>
                <span data-testid="caplang-hint" className="text-xs text-muted">
                  {capLang === '' ? t('dest.capLangOrigHint') : capLang === streamLang ? t('translate.same') : t('dest.capLangDelayHint')}
                </span>
              </label>
            )}
          </div>
        )}

        {showTc && dest && (
          <div data-testid="transcode-settings" className="flex flex-col gap-3 rounded-md border border-line-2 p-3">
            <Switch label={t('tc.switch', { platform: PLATFORM_LABEL[dest.platform] })} on={tcOn} onChange={(v) => { setTcOn(v); setSaveError(''); }}
              sub={t('tc.switchSub', { platform: PLATFORM_LABEL[dest.platform] })} />
          </div>
        )}

        {LANGUAGES && dest && (
          <div className="rounded-md border border-dashed border-line-2 p-3">
            <LanguageSettings value={lang} onChange={setLang} platform={dest.platform} live={live} />
          </div>
        )}
      </div>

      {saveError && <Banner tone="err">{saveError}{isNew ? '' : ' Your old settings are still live.'}</Banner>}
      {!saveError && dirty && !confirm && !invalid && (
        <Banner tone="info">
          {isNew ? (streamLive ? `You're live, so ${name} starts as soon as you add it.` : `${name} goes live when you start streaming.`)
            : tracksOnly ? t('dest.trackNote')
            : captionsOnly ? (live ? 'Saving changes only the captions: the video keeps streaming.' : 'Captions settings apply the next time it goes live.')
            : settingsOnly && tcDirty && !capDirty && !fillDirty && !graceDirty ? t('tc.saveNote')
            : settingsOnly && capDirty ? (live ? 'The changes apply right away, without reconnecting.' : 'Saved settings apply the next time it goes live.')
            : settingsOnly ? (fillDirty ? (live ? 'The new bitrate applies right away, without reconnecting.' : 'The new bitrate applies the next time it goes live.') : 'The new hold applies without reconnecting.')
            : live ? `Saving reconnects only ${name}, about 2 s off air there. Other destinations keep streaming.` : 'Saved settings apply the next time it goes live.'}
        </Banner>
      )}

      {confirm === 'remove' && dest && (
        <InlineConfirm tone="bad" title={`Remove ${dest.name}?`}
          body={live ? "It goes off air right away. Other destinations keep streaming. This can't be undone." : "Its settings and key will be deleted. This can't be undone."}
          no="Keep it" yes="Remove" onNo={() => setConfirm(undefined)} onYes={() => void remove()} />
      )}
      {confirm === 'protocol' && dest && (
        <InlineConfirm tone="warn" title={t('proto.confirm.title', { platform: PLATFORM_LABEL[dest.platform] })}
          body={t('proto.confirm.body', { name, proto: protocolName(protocols, protocol) })}
          no={t('proto.confirm.no', { proto: protocolName(protocols, proto0) })} yes={t('proto.confirm.yes')}
          onNo={() => { pickProtocol(proto0); setConfirm(undefined); }} onYes={() => void submit(undefined, true)} />
      )}
      {confirm === 'captions-lang' && dest && (
        <InlineConfirm tone="warn" title={t('dest.capLangConfirm.title', { name })}
          body={capLang ? t('dest.capLangConfirm.toTr', { lang: langName(capLang) }) : t('dest.capLangConfirm.toOrig')}
          no={t('dest.capLangConfirm.no')} yes={t('dest.capLangConfirm.yes')}
          onNo={() => { setCapLang(capLang0); setConfirm(undefined); }} onYes={() => void submit(undefined, true)} />
      )}
      {confirm === 'discard' && (
        <InlineConfirm tone="warn" title="Discard your changes?" body={`${dest?.name ?? 'This destination'} keeps its current settings.`}
          no="Keep editing" yes="Discard" onNo={() => setConfirm(undefined)} onYes={onCancel} />
      )}

      {!confirm && (
        <>
          <div className="grid grid-cols-2 gap-2">
            <SecondaryButton disabled={saving} onClick={cancel}>Cancel</SecondaryButton>
            <PrimaryButton type="submit" busy={saving} disabled={!dirty || invalid}>
              {saving ? 'Saving…' : saveError ? 'Try again' : isNew ? (streamLive ? 'Add and go live' : 'Add destination') : dest?.needsKeyFix ? 'Save and reconnect' : 'Save'}
            </PrimaryButton>
          </div>
          {!isNew && <DangerLink disabled={saving} onClick={() => setConfirm('remove')}>Remove destination…</DangerLink>}
        </>
      )}
    </form>
  );
}
